import Phaser from 'phaser';
import { Lipid } from './Lipid';
import { Molecule } from './Molecule';

export class ProtoCell extends Phaser.GameObjects.Container {
    private boundary: Phaser.GameObjects.Ellipse;
    private assembledCell: Phaser.GameObjects.Ellipse;
    private centerX: number;
    private centerY: number;
    private radius: number;
    private boundaryThickness: number = 150;
    
    // Track assembly progress
    private lipidsInPosition: number = 0;
    private moleculesInPosition: number = 0;
    private neededLipids: number = 20;
    private neededMolecules: number = 8;
    
    // Define slots for lipids
    private lipidSlots: Array<{
        angle: number;
        occupied: boolean;
        marker?: Phaser.GameObjects.Arc;
    }> = [];
    
    // Flag to track whether auto-assembly mode is active (slots are enforced)
    private slotAssignmentActive: boolean = false;
    
    // Event emitters
    private onAssemblyProgressChange: Phaser.Events.EventEmitter;
    private onAssemblyComplete: Phaser.Events.EventEmitter;
    
    constructor(scene: Phaser.Scene, x: number, y: number, radius: number = 150) {
        super(scene, x, y);
        
        this.centerX = x;
        this.centerY = y;
        this.radius = radius;
        
        // Initialize event emitters
        this.onAssemblyProgressChange = new Phaser.Events.EventEmitter();
        this.onAssemblyComplete = new Phaser.Events.EventEmitter();
        
        // Create dashed boundary
        this.boundary = scene.add.ellipse(0, 0, this.radius * 2, this.radius * 2);
        this.boundary.setStrokeStyle(2, 0xffffff, 0.4);
        this.boundary.setFillStyle(0xffffff, 0);
        
        // Create assembled cell background (initially invisible)
        this.assembledCell = scene.add.ellipse(0, 0, this.radius * 2, this.radius * 2);
        this.assembledCell.setFillStyle(0xffffff, 0.1);
        this.assembledCell.setAlpha(0);
        
        // Add to container
        this.add([this.assembledCell, this.boundary]);
        
        // Create lipid slots
        this.createLipidSlots();
        
        // Visualize slots (comment out in production)
        this.visualizeSlots();
        
        // Add to scene
        scene.add.existing(this);
        
        // Set depth to ensure it's above the background but below UI
        this.setDepth(1);
        
        // Make sure the ProtoCell doesn't block input
        // We want to be able to click through it to drag components
        // Completely disable interactivity
        this.disableInteractive();
        
        console.log('ProtoCell created, interactive:', this.input?.enabled);
    }
    
    private createLipidSlots(): void {
        // Clear any existing slots
        this.lipidSlots = [];
        
        // Create evenly spaced slots around the membrane
        for (let i = 0; i < this.neededLipids; i++) {
            const angle = (i / this.neededLipids) * Math.PI * 2;
            
            this.lipidSlots.push({
                angle: angle,
                occupied: false
            });
        }
        
        console.log(`Created ${this.lipidSlots.length} lipid slots around the membrane`);
    }
    
    // Debug method to visualize slots
    private visualizeSlots(): void {
        console.log('Visualizing lipid slots');
        
        this.lipidSlots.forEach((slot, index) => {
            // Calculate position
            const x = Math.cos(slot.angle) * this.radius;
            const y = Math.sin(slot.angle) * this.radius;
            
            // Create a visible marker with the same color as the boundary ring
            // Use white with 0.4 opacity to match the boundary ring
            const marker = this.scene.add.circle(x, y, 4, 0xffffff, slot.occupied ? 0.6 : 0.4);
            this.add(marker);
            
            // Store reference to marker
            slot.marker = marker;
        });
    }
    
    // Find nearest available slot to a given position
    private findNearestAvailableSlot(x: number, y: number): { index: number, angle: number } | null {
        // Convert to local coordinates relative to the cell center
        const localX = x - this.centerX;
        const localY = y - this.centerY;
        
        // Find slot with closest physical distance
        let closestSlotIndex = -1;
        let minDistance = Number.MAX_VALUE;
        
        this.lipidSlots.forEach((slot, index) => {
            if (!slot.occupied) {
                // Calculate position of the slot
                const slotX = this.centerX + Math.cos(slot.angle) * this.radius;
                const slotY = this.centerY + Math.sin(slot.angle) * this.radius;
                
                // Calculate Euclidean distance from the lipid to this slot
                const dx = x - slotX;
                const dy = y - slotY;
                const distance = Math.sqrt(dx * dx + dy * dy);
                
                if (distance < minDistance) {
                    minDistance = distance;
                    closestSlotIndex = index;
                }
            }
        });
        
        if (closestSlotIndex >= 0) {
            console.log(`Found closest slot ${closestSlotIndex} at distance ${minDistance.toFixed(2)}`);
            return {
                index: closestSlotIndex,
                angle: this.lipidSlots[closestSlotIndex].angle
            };
        }
        
        return null;
    }
    
    // Occupy a specific slot
    private occupySlot(index: number): void {
        if (index >= 0 && index < this.lipidSlots.length) {
            this.lipidSlots[index].occupied = true;
            
            // Update visualization if it exists
            if (this.lipidSlots[index].marker) {
                // Make occupied slots more visible with higher opacity
                this.lipidSlots[index].marker.setAlpha(0.6);
            }
        }
    }
    
    // Free a specific slot
    private freeSlot(index: number): void {
        if (index >= 0 && index < this.lipidSlots.length) {
            this.lipidSlots[index].occupied = false;
            
            // Update visualization if it exists
            if (this.lipidSlots[index].marker) {
                // Make available slots slightly transparent
                this.lipidSlots[index].marker.setAlpha(0.4);
            }
        }
    }
    
    public get progress(): number {
        const totalNeeded = this.neededLipids + this.neededMolecules;
        const currentTotal = this.lipidsInPosition + this.moleculesInPosition;
        return Math.min(100, Math.round((currentTotal / totalNeeded) * 100));
    }
    
    public get isComplete(): boolean {
        return this.lipidsInPosition >= this.neededLipids && 
               this.moleculesInPosition >= this.neededMolecules;
    }
    
    public get progressEmitter(): Phaser.Events.EventEmitter {
        return this.onAssemblyProgressChange;
    }
    
    public get completeEmitter(): Phaser.Events.EventEmitter {
        return this.onAssemblyComplete;
    }
    
    public setNeededComponents(lipids: number, molecules: number): void {
        this.neededLipids = lipids;
        this.neededMolecules = molecules;
        
        // Recreate slots if needed
        this.createLipidSlots();
    }
    
    public checkComponentPosition(component: Lipid | Molecule): boolean {
        // Get component's position relative to the cell center
        const dx = component.x - this.centerX;
        const dy = component.y - this.centerY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        console.log(`Checking ${component.getData('type')} position: distance=${distance.toFixed(2)}, centerX=${this.centerX}, centerY=${this.centerY}`);
        
        if (component.getData('type') === 'lipid') {
            // Lipids should be positioned near the boundary
            const minDistance = this.radius - 10;
            const maxDistance = this.radius + 10;
            console.log(`Lipid boundary check: min=${minDistance}, max=${maxDistance}, actual=${distance.toFixed(2)}`);
            
            if (distance > minDistance && distance < maxDistance) {
                // For manually dragged lipids, always snap to the nearest available slot
                // Find nearest available slot
                const slot = this.findNearestAvailableSlot(component.x, component.y);
                
                if (slot) {
                    console.log(`Snapping lipid to nearest available slot ${slot.index}`);
                    
                    // Get the current slot index if it exists
                    const currentSlotIndex = component.getData('slotIndex');
                    
                    // Free the current slot if it's different
                    if (currentSlotIndex !== undefined && currentSlotIndex !== slot.index) {
                        this.freeSlot(currentSlotIndex);
                    }
                    
                    // Position lipid at the new slot
                    this.positionLipidAtSlot(component as Lipid, slot.index);
                    
                    // Create a temporary highlight effect at the slot
                    // First get the slot coordinates
                    const slotX = this.centerX + Math.cos(slot.angle) * this.radius;
                    const slotY = this.centerY + Math.sin(slot.angle) * this.radius;
                    
                    // Create a highlight circle with the same color as the boundary but brighter
                    const highlight = this.scene.add.circle(slotX, slotY, 12, 0xffffff, 0.8);
                    this.add(highlight);
                    
                    // Make it pulse and fade out
                    this.scene.tweens.add({
                        targets: highlight,
                        scaleX: 1.8,
                        scaleY: 1.8,
                        alpha: 0,
                        duration: 500,
                        ease: 'Power2',
                        onComplete: () => {
                            highlight.destroy();
                        }
                    });
                    
                    // Add a small bounce effect when snapping
                    this.scene.tweens.add({
                        targets: component,
                        scaleX: { from: 1.2, to: 1 },
                        scaleY: { from: 1.2, to: 1 },
                        duration: 200,
                        ease: 'Back.easeOut'
                    });
                    
                    return true;
                } else {
                    // If no slots available, handle gracefully
                    console.log('No available slots for lipid');
                    
                    // We could shake the lipid to indicate it can't be placed
                    this.scene.tweens.add({
                        targets: component,
                        x: component.x + 5,
                        y: component.y,
                        yoyo: true,
                        repeat: 3,
                        duration: 50,
                        ease: 'Sine.easeInOut',
                        onComplete: () => {
                            // Reset to original position
                            component.x = component.x;
                            component.y = component.y;
                        }
                    });
                    
                    return false;
                }
            } else if (component.isCorrectlyPositioned()) {
                // If it was previously positioned but now moved away
                // Get the slot index from the component data
                const slotIndex = component.getData('slotIndex');
                if (slotIndex !== undefined) {
                    console.log(`Freeing slot ${slotIndex}`);
                    this.freeSlot(slotIndex);
                }
                
                component.setPositioned(false);
                this.lipidsInPosition--;
                this.emitProgressUpdate();
            }
        } else if (component.getData('type') === 'molecule') {
            // Molecules should be positioned inside the cell
            const maxInnerDistance = this.radius * 0.8;
            console.log(`Molecule inner check: max=${maxInnerDistance}, actual=${distance.toFixed(2)}`);
            
            if (distance < maxInnerDistance) {
                if (!component.isCorrectlyPositioned()) {
                    component.setPositioned(true);
                    this.moleculesInPosition++;
                    this.emitProgressUpdate();
                }
                return true;
            } else if (component.isCorrectlyPositioned()) {
                // If it was previously positioned but now moved away
                component.setPositioned(false);
                this.moleculesInPosition--;
                this.emitProgressUpdate();
            }
        }
        
        return false;
    }
    
    private positionLipidAtSlot(lipid: Lipid, slotIndex: number): void {
        if (slotIndex < 0 || slotIndex >= this.lipidSlots.length) {
            console.error(`Invalid slot index: ${slotIndex}`);
            return;
        }
        
        const slot = this.lipidSlots[slotIndex];
        
        // Only position if not already positioned
        if (!lipid.isCorrectlyPositioned()) {
            // Calculate position based on slot angle
            const angle = slot.angle;
            
            // Snap lipid to the boundary and point toward center
            lipid.snapToMembrane(this.centerX, this.centerY, angle, this.radius);
            
            // Mark slot as occupied and store the slot index in the lipid
            this.occupySlot(slotIndex);
            lipid.setData('slotIndex', slotIndex);
            
            this.lipidsInPosition++;
            this.emitProgressUpdate();
        }
    }
    
    private positionLipid(lipid: Lipid, dx: number, dy: number, distance: number): void {
        // Only position if not already positioned
        if (!lipid.isCorrectlyPositioned()) {
            // Calculate angle for precise positioning
            const angle = Math.atan2(dy, dx);
            
            // Find the nearest available slot
            const slot = this.findNearestAvailableSlot(lipid.x, lipid.y);
            
            if (slot) {
                // Snap lipid to the boundary and point toward center
                lipid.snapToMembrane(this.centerX, this.centerY, slot.angle, this.radius);
                
                // Mark slot as occupied and store slot index
                this.occupySlot(slot.index);
                lipid.setData('slotIndex', slot.index);
                
                this.lipidsInPosition++;
                this.emitProgressUpdate();
            } else {
                // If no available slot, use the original angle
                lipid.snapToMembrane(this.centerX, this.centerY, angle, this.radius);
                
                this.lipidsInPosition++;
                this.emitProgressUpdate();
            }
        }
    }
    
    private emitProgressUpdate(): void {
        // Update visual representation of cell assembly
        const lipidProgress = Math.min(1, this.lipidsInPosition / this.neededLipids);
        this.assembledCell.setAlpha(lipidProgress * 0.8);
        
        // Emit progress event
        this.onAssemblyProgressChange.emit('progress', this.progress);
        
        // Check if assembly is complete
        this.checkAssemblyCompletion();
    }
    
    private checkAssemblyCompletion(): void {
        if (this.isComplete) {
            // Make cell glow on completion
            this.scene.tweens.add({
                targets: this.assembledCell,
                alpha: { from: 0.8, to: 1 },
                fillAlpha: { from: 0.2, to: 0.4 },
                yoyo: true,
                repeat: -1,
                duration: 1000,
                ease: 'Sine.easeInOut'
            });
            
            // Emit completion event
            this.onAssemblyComplete.emit('complete');
        }
    }
    
    // Reset the assembly process
    public reset(): void {
        this.lipidsInPosition = 0;
        this.moleculesInPosition = 0;
        this.assembledCell.setAlpha(0);
        
        // Reset all slots
        this.lipidSlots.forEach((slot, index) => {
            slot.occupied = false;
            if (slot.marker) {
                slot.marker.setAlpha(0.4);
            }
        });
        
        // Deactivate slot assignment mode
        this.slotAssignmentActive = false;
        
        // Stop any active tweens
        this.scene.tweens.killTweensOf(this.assembledCell);
        
        // Emit progress event
        this.emitProgressUpdate();
    }
    
    // Auto-assemble the cell (for debug or auto-completion)
    public autoAssemble(components: (Lipid | Molecule)[]): void {
        console.log('Auto-assembling with', components.length, 'components');
        
        // Activate slot assignment mode
        this.slotAssignmentActive = true;
        
        // STEP 1: Move any already positioned lipids to proper slots
        // Get all positioned lipids that don't have a slot assigned
        const positionedLipidsWithoutSlot = components.filter(
            component => component.getData('type') === 'lipid' && 
                          component.isCorrectlyPositioned() &&
                          component.getData('slotIndex') === undefined
        ) as Lipid[];
        
        // Assign them to slots first
        if (positionedLipidsWithoutSlot.length > 0) {
            console.log(`Found ${positionedLipidsWithoutSlot.length} lipids already positioned but without slots`);
            
            // Convert them from manual positioning to slot assignment with animation
            positionedLipidsWithoutSlot.forEach(lipid => {
                // Find the nearest available slot to the lipid's current position
                const slot = this.findNearestAvailableSlot(lipid.x, lipid.y);
                
                if (slot) {
                    // Get the current angle of the lipid relative to the center
                    const dx = lipid.x - this.centerX;
                    const dy = lipid.y - this.centerY;
                    const currentAngle = Math.atan2(dy, dx);
                    
                    // Calculate the target position based on the slot
                    const targetX = this.centerX + Math.cos(slot.angle) * this.radius;
                    const targetY = this.centerY + Math.sin(slot.angle) * this.radius;
                    
                    console.log(`Transitioning manually positioned lipid from angle ${currentAngle.toFixed(2)} to slot ${slot.index} at angle ${slot.angle.toFixed(2)}`);
                    
                    // Occupy the slot right away so it's not assigned to another lipid
                    this.occupySlot(slot.index);
                    
                    // Animate the lipid to its new slot position
                    this.scene.tweens.add({
                        targets: lipid,
                        x: targetX,
                        y: targetY,
                        rotation: slot.angle + Math.PI/2, // Point outward
                        duration: 800,
                        ease: 'Power2',
                        onComplete: () => {
                            // Finalize the lipid's slot assignment
                            lipid.setData('slotIndex', slot.index);
                            console.log(`Manually positioned lipid moved to slot ${slot.index}`);
                        }
                    });
                } else {
                    console.log(`No available slot found for manually positioned lipid`);
                }
            });
        }
        
        // STEP 2: Identify free slots (that aren't already occupied)
        const availableSlots = this.lipidSlots
            .map((slot, index) => ({ slot, index }))
            .filter(item => !item.slot.occupied);
            
        console.log(`Found ${availableSlots.length} available slots for lipids`);
        
        // Find all unpositioned lipids
        const unpositionedLipids = components.filter(
            component => component.getData('type') === 'lipid' && !component.isCorrectlyPositioned()
        ) as Lipid[];
        
        console.log(`Found ${unpositionedLipids.length} unpositioned lipids`);
        
        // STEP 3: Assign each free slot to an unattached lipid
        // Shuffle available slots to randomize placement
        const shuffledSlots = [...availableSlots];
        Phaser.Utils.Array.Shuffle(shuffledSlots);
        
        // Reserve slots for lipids
        const lipidAssignments: { lipid: Lipid, slotIndex: number, angle: number }[] = [];
        
        // Math.min to handle the case where we have more lipids than slots
        const maxLipidsToAssign = Math.min(unpositionedLipids.length, shuffledSlots.length);
        
        for (let i = 0; i < maxLipidsToAssign; i++) {
            const lipid = unpositionedLipids[i];
            const { slot, index } = shuffledSlots[i];
            
            // Immediately mark slot as occupied so other lipids can't target it
            this.occupySlot(index);
            
            // Store the assignment for animation
            lipidAssignments.push({
                lipid,
                slotIndex: index,
                angle: slot.angle
            });
            
            console.log(`Assigned lipid ${i} to slot ${index} at angle ${slot.angle.toFixed(2)}`);
        }
        
        // STEP 4: Make lipids slowly float to their slots with custom rotation
        lipidAssignments.forEach((assignment, i) => {
            const { lipid, angle, slotIndex } = assignment;
            
            // Calculate target position on membrane
            const targetX = this.centerX + Math.cos(angle) * this.radius;
            const targetY = this.centerY + Math.sin(angle) * this.radius;
            
            // Calculate a midpoint for a more interesting path
            // Midpoint is slightly randomized and closer to the center
            const midDistance = this.radius * (0.4 + Math.random() * 0.4); // 40-80% of the radius
            const midAngle = angle + (Math.random() * 0.5 - 0.25); // Slightly varied angle
            const midX = this.centerX + Math.cos(midAngle) * midDistance;
            const midY = this.centerY + Math.sin(midAngle) * midDistance;
            
            // Calculate a random rotation speed (rotations per second)
            const rotationSpeed = 0.5 + Math.random() * 2; // 0.5 to 2.5 rotations per second
            
            // Kill any existing tweens on this lipid
            this.scene.tweens.killTweensOf(lipid);
            
            // Store initial rotation to animate from
            const initialRotation = lipid.rotation;
            
            // Store the slot data in the lipid for later use
            lipid.setData('targetSlotIndex', slotIndex);
            lipid.setData('assignedAngle', angle);
            
            // Path 1: Move to midpoint with free rotation
            this.scene.tweens.add({
                targets: lipid,
                x: midX,
                y: midY,
                duration: 1000 + Math.random() * 1000, // 1-2 seconds
                ease: 'Sine.easeInOut',
                onUpdate: () => {
                    // Apply continuous rotation during movement
                    lipid.rotation += rotationSpeed * (Math.PI * 2) / 60; // Assuming 60fps
                },
                onComplete: () => {
                    // Path 2: Move from midpoint to final position and correct orientation
                    this.scene.tweens.add({
                        targets: lipid,
                        x: targetX,
                        y: targetY,
                        rotation: angle + Math.PI/2, // Final orientation
                        duration: 800 + Math.random() * 800, // 0.8-1.6 seconds
                        ease: 'Power2',
                        onComplete: () => {
                            // STEP 5: Finalize the placement and mark slot as occupied
                            // Snap lipid precisely to the membrane
                            lipid.snapToMembrane(this.centerX, this.centerY, angle, this.radius);
                            
                            // Store slot index in the lipid
                            lipid.setData('slotIndex', slotIndex);
                            
                            // Increment our counter
                            this.lipidsInPosition++;
                            
                            // Update progress
                            this.emitProgressUpdate();
                            
                            console.log(`Lipid ${i} placed in slot ${slotIndex}`);
                        }
                    });
                }
            });
        });
        
        // Handle molecules separately - position them inside the cell
        const unpositionedMolecules = components.filter(
            component => component.getData('type') === 'molecule' && !component.isCorrectlyPositioned()
        ) as Molecule[];
        
        // Distribute molecules randomly inside the cell
        for (let i = 0; i < Math.min(unpositionedMolecules.length, this.neededMolecules - this.moleculesInPosition); i++) {
            const molecule = unpositionedMolecules[i];
            
            // Random target position inside the cell
            const angle = Math.random() * Math.PI * 2;
            const distance = Math.random() * (this.radius * 0.7);
            const targetX = this.centerX + Math.cos(angle) * distance;
            const targetY = this.centerY + Math.sin(angle) * distance;
            
            // Calculate a midpoint for more interesting path
            const midDistance = distance * 1.5; // Path that swings wider
            const midAngle = angle + (Math.random() * 1 - 0.5); // Varied angle
            const midX = this.centerX + Math.cos(midAngle) * midDistance;
            const midY = this.centerY + Math.sin(midAngle) * midDistance;
            
            // Kill any existing tweens
            this.scene.tweens.killTweensOf(molecule);
            
            // Path 1: Move to midpoint
            this.scene.tweens.add({
                targets: molecule,
                x: midX,
                y: midY,
                duration: 1200 + Math.random() * 800,
                ease: 'Sine.easeInOut',
                onComplete: () => {
                    // Path 2: Move from midpoint to final position
                    this.scene.tweens.add({
                        targets: molecule,
                        x: targetX,
                        y: targetY,
                        duration: 800 + Math.random() * 400,
                        ease: 'Power2',
                        onComplete: () => {
                            // Mark as positioned
                            molecule.setPositioned(true);
                            this.moleculesInPosition++;
                            this.emitProgressUpdate();
                            
                            console.log(`Molecule positioned inside cell`);
                        }
                    });
                }
            });
        }
    }

    getRadius(): number {
        return this.radius;
    }
} 